// Plotly, loaded only when a plot is on screen (it is large).
import { Loader, useComputedColorScheme } from "@mantine/core";
import { useEffect, useRef, useState } from "react";

type PlotlyModule = {
  react: (el: HTMLElement, data: unknown[], layout: unknown, config?: unknown) => Promise<unknown>;
  purge: (el: HTMLElement) => void;
  toImage: (fig: { data: unknown[]; layout: unknown }, opts: Record<string, unknown>) => Promise<string>;
  Plots: { resize: (el: HTMLElement) => void };
  Icons: Record<string, unknown>;
};

/** A file name from the figure's title ("Energy Spectrum vs Magnetic Field (n=1 electrons)" -> energy_spectrum_vs...). */
export function imageName(layout: Record<string, any>): string {
  const t = String(layout.title?.text ?? layout.title ?? "plot").replace(/<[^>]*>/g, "");
  return t.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 60) || "plot";
}

let plotly: Promise<PlotlyModule> | null = null;
function loadPlotly(): Promise<PlotlyModule> {
  plotly ??= import("plotly.js-dist-min").then((m: any) => (m.default ?? m) as PlotlyModule);
  return plotly;
}

export const PALETTE = ["#2a78d6", "#eb6834", "#1baf7a", "#eda100", "#e87ba4", "#008300", "#4a3aa7", "#e34948"];

// --- light / dark ------------------------------------------------------------------------------------------------
// The figures are written for a light page (dark text, light grid, white-ish label boxes). On a dark page the same
// layout is adapted here, so every plot follows the app's colour scheme without each figure knowing about it.
const DARK = { text: "#d4d4d8", grid: "rgba(255,255,255,0.13)", line: "rgba(255,255,255,0.35)", box: "rgba(36,36,40,0.85)",
  border: "#6b6b70", boxText: "#e4e4e7" };

function rgb(color: unknown): [number, number, number] | null {
  if (typeof color !== "string") return null;
  const c = color.trim().toLowerCase();
  if (c === "white") return [255, 255, 255];
  if (c === "whitesmoke") return [245, 245, 245];
  let m = /^#([0-9a-f]{3})$/.exec(c);
  if (m) return [0, 1, 2].map((i) => parseInt(m![1][i] + m![1][i], 16)) as [number, number, number];
  m = /^#([0-9a-f]{6})$/.exec(c);
  if (m) return [0, 2, 4].map((i) => parseInt(m![1].slice(i, i + 2), 16)) as [number, number, number];
  m = /^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/.exec(c);
  return m ? [Number(m[1]), Number(m[2]), Number(m[3])] : null;
}
const luminance = (c: unknown) => { const v = rgb(c); return v ? (0.2126 * v[0] + 0.7152 * v[1] + 0.0722 * v[2]) / 255 : null; };
const isLight = (c: unknown) => (luminance(c) ?? 0) > 0.7;
const isDarkText = (c: unknown) => { const l = luminance(c); return l !== null && l < 0.45; };

/** The layout for the page's colour scheme: unchanged on a light page; on a dark page light text and axes, dark
 * label boxes (instead of white ones) and a readable toolbar. */
export function themeLayout(layout: Record<string, any>, dark: boolean): Record<string, any> {
  if (!dark) return layout;
  const out: Record<string, any> = { ...layout, font: { ...(layout.font ?? {}), color: DARK.text },
    modebar: { bgcolor: "rgba(0,0,0,0)", color: "#9a9aa0", activecolor: DARK.boxText, ...(layout.modebar ?? {}) } };
  for (const [key, ax] of Object.entries(layout)) {
    if (!/^[xy]axis\d*$/.test(key) || !ax || typeof ax !== "object") continue;
    out[key] = { ...ax, gridcolor: DARK.grid, zerolinecolor: DARK.line, linecolor: DARK.line,
      ...(ax.title?.font?.color && isDarkText(ax.title.font.color) ? { title: { ...ax.title, font: { ...ax.title.font, color: DARK.text } } } : {}) };
  }
  if (layout.legend) {
    out.legend = { ...layout.legend, ...(isLight(layout.legend.bgcolor) ? { bgcolor: DARK.box } : {}) };
  }
  if (layout.title?.font?.color && isDarkText(layout.title.font.color)) {
    out.title = { ...layout.title, font: { ...layout.title.font, color: DARK.text } };
  }
  if (Array.isArray(layout.annotations)) {
    out.annotations = layout.annotations.map((a: any) => ({
      ...a,
      ...(isLight(a.bgcolor) ? { bgcolor: DARK.box } : {}),
      ...(isLight(a.bgcolor) && a.bordercolor ? { bordercolor: DARK.border } : {}),
      ...(a.font?.color && isDarkText(a.font.color) ? { font: { ...a.font, color: DARK.boxText } } : {}),
    }));
  }
  return out;
}

/** The traces for the page's colour scheme: on a dark page, lines and markers drawn in (near-)black - e.g. the DFT
 * bands under a tight-binding fit - become light, so they stay visible; every other colour is kept. */
export function themeData(data: unknown[], dark: boolean): unknown[] {
  if (!dark) return data;
  const fix = (c: unknown) => { const l = luminance(c); return l !== null && l < 0.2 ? DARK.text : c; };
  return data.map((t: any) => {
    if (!t || typeof t !== "object") return t;
    let out = t;
    if (t.line && typeof t.line.color === "string" && fix(t.line.color) !== t.line.color) out = { ...out, line: { ...t.line, color: fix(t.line.color) } };
    if (t.marker && typeof t.marker.color === "string" && fix(t.marker.color) !== t.marker.color) out = { ...out, marker: { ...t.marker, color: fix(t.marker.color) } };
    return out;
  });
}

export function Plot({ data, layout, height = 380, editableShapes = false, onRelayout }: {
  data: unknown[]; layout: Record<string, unknown>; height?: number;
  editableShapes?: boolean;                                 // shapes can be dragged (the Wannier windows)
  onRelayout?: (update: Record<string, unknown>) => void;   // plotly_relayout: e.g. {"shapes[1].y1": 2.3}
}) {
  const el = useRef<HTMLDivElement>(null);
  const relayout = useRef(onRelayout);
  relayout.current = onRelayout;
  const bound = useRef(false);
  const [loading, setLoading] = useState(true);
  const dark = useComputedColorScheme("light") === "dark";

  useEffect(() => {
    let alive = true;
    const node = el.current;
    loadPlotly().then((P) => {
      if (!alive || !node) return;
      setLoading(false);
      const base = {
        height,
        margin: { l: 60, r: 20, t: 40, b: 50 },
        font: { family: "system-ui, sans-serif", size: 12 },
        colorway: PALETTE,
        paper_bgcolor: "rgba(0,0,0,0)",
        plot_bgcolor: "rgba(0,0,0,0)",
        ...layout,
      };
      // the PNG download is always the light figure on white (readable in papers and slides), whatever the page's scheme
      const download = {
        name: "download-png", title: "Download plot as a png", icon: P.Icons.camera,
        click: () => P.toImage({ data, layout: { ...base, paper_bgcolor: "white", plot_bgcolor: "white" } },
          { format: "png", scale: 3, width: node.offsetWidth || 800, height: (base.height as number) ?? height })
          .then((url) => {
            const a = document.createElement("a");
            a.href = url;
            a.download = `${imageName(base)}.png`;
            a.click();
          }),
      };
      P.react(node, themeData(data, dark), themeLayout(base, dark), { responsive: true, displaylogo: false,
        modeBarButtonsToRemove: ["toImage"], modeBarButtonsToAdd: [download],
        ...(editableShapes ? { edits: { shapePosition: true } } : {}) }).then(() => {
        const n = node as unknown as { on?: (ev: string, f: (u: Record<string, unknown>) => void) => void };
        if (!bound.current && n.on) {
          bound.current = true;
          n.on("plotly_relayout", (u) => relayout.current?.(u));
        }
      });
    });
    return () => {
      alive = false;
    };
  }, [data, layout, height, dark, editableShapes]);

  useEffect(() => {
    const node = el.current;
    return () => {
      if (node) loadPlotly().then((P) => P.purge(node));
    };
  }, []);

  return (
    <div style={{ position: "relative", minHeight: (layout.height as number | undefined) ?? height }}>
      {loading && <Loader size="sm" style={{ position: "absolute", top: 16, left: 16 }} />}
      <div ref={el} data-testid="plot" />
    </div>
  );
}
